import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ref, get } from "@firebase/database";
import { useFirebase } from "../../contexts/FirebaseContext";
import { Center, Spinner, Text, VStack } from "@chakra-ui/react";

interface PlayerGateProps {
  children: React.ReactNode;
}

/**
 * Lightweight gate that checks if a game is full before allowing access.
 * Does a ONE-TIME check of /gameMeta/{gameId} which contains maxPlayers, playerCount, and playerNames.
 * Allows existing players (based on localStorage) through even if the game is full.
 * Redirects new visitors to audience view if the game has reached max players.
 * This runs OUTSIDE of GameContext to avoid downloading the full game state.
 */
export const PlayerGate: React.FC<PlayerGateProps> = ({ children }) => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { db } = useFirebase();
  const [isChecking, setIsChecking] = useState(true);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (!gameId) {
      setShouldRender(true);
      setIsChecking(false);
      return;
    }

    // Check if user is already a player (stored in localStorage)
    const storedPlayerName = localStorage.getItem("currentPlayerName");

    // One-time check of lightweight metadata path
    const metaRef = ref(db, `gameMeta/${gameId}`);
    get(metaRef)
      .then((snapshot) => {
        const meta = snapshot.val();

        if (meta) {
          const { maxPlayers, playerCount, playerNames = [] } = meta;

          // If user is already a player in this game, allow them through
          if (storedPlayerName && playerNames.includes(storedPlayerName)) {
            setShouldRender(true);
            setIsChecking(false);
            return;
          }

          // If game has a max and is full, redirect new visitors to audience
          if (maxPlayers != null && playerCount >= maxPlayers) {
            navigate(`/audience/${gameId}`, { replace: true });
            return;
          }
        }

        // Game meta doesn't exist (no limit) or isn't full, allow rendering
        setShouldRender(true);
        setIsChecking(false);
      })
      .catch((error) => {
        console.error("Error checking game meta in PlayerGate:", error);
        // On error, allow through (let the actual game handle it)
        setShouldRender(true);
        setIsChecking(false);
      });
  }, [gameId, db, navigate]);

  if (isChecking) {
    return (
      <Center height="100vh" bg="jeopardy.blue">
        <VStack spacing={4}>
          <Spinner size="xl" color="white" />
          <Text textStyle="clue" color="white">
            Checking game...
          </Text>
        </VStack>
      </Center>
    );
  }

  if (!shouldRender) {
    return null;
  }

  return <>{children}</>;
};
